import { AsyncStateStatus } from '../root.types'
import { UserType } from '../../application/domain/utils/user.type'
import { User } from '../../application/domain/models/entity/user'

/**
 * Redux Actions Company
 * @typedef UsersTypes
 * @namespace UsersTypes
 * @category React
 * @subcategory Redux / Types
 */

/**
 * Typing constant of action identifiers.
 * </br>
 * The action identifiers are:
 * <pre>
 *     <ul>
 *         <li>@users/userRequest</li>
 *         <li>@users/adminRequest</li>
 *         <li>@users/newAdminRequest</li>
 *         <li>@users/editAdminRequest</li>
 *         <li>@users/analystRequest</li>
 *         <li>@users/newAnalystRequest</li>
 *         <li>@users/editAnalystRequest</li>
 *         <li>@users/newOrgRequest</li>
 *         <li>@users/orgsRequest</li>
 *         <li>@users/getUersByOrg</li>
 *         <li>@users/orgResponsibles</li>
 *         <li>@users/orgsFindRequest</li>
 *         <li>@users/orgsUpdateRequest</li>
 *      </ul>
 * </pre>
 * @memberof UsersTypes
 * @function
 * @category React
 * @subcategory Redux / Types
 */
export enum UsersTypes {
    USER_REQUEST = '@users/userRequest',
    ADMIN_REQUEST = '@users/adminRequest',
    NEW_ADMIN = '@users/newAdminRequest',
    EDIT_ADMIN = '@users/editAdminRequest',
    ANALYST_REQUEST = '@users/analystRequest',
    NEW_ANALYST = '@users/newAnalystRequest',
    EDIT_ANALYST = '@users/editAnalystRequest',
    USER_ACTIVE_REQUEST = '@users/userActiveRequest',
    NEW_ORG = '@users/newOrgRequest',
    ORGS_REQUEST = '@users/orgsRequest',
    ORGS_USERS_REQUEST = '@users/getUersByOrg',
    ORGS_RESPONSIBLES_REQUEST =  '@users/orgResponsibles',
    ORGS_FIND_REQUEST = '@users/orgsFindRequest',
    ORGS_UPDATE_REQUEST = '@users/orgsUpdateRequest',
    ORGS_ACT_UPLOAD = '@users/orgsActUpload',
    ORGS_ACT_DOWNLOAD = '@users/orgsActDownload',
    ORGS_ACT_DELETE = '@users/orgsActDelete',
    SIGNUP_REQUEST = '@users/signupRequest',
    USERS_REQUEST = '@users/usersRequest',
    DELETE_REQUEST = '@users/deleteUsersRequest',
    FIND_USER_REQUEST = '@users/findUserRequest',
    ORG_NAME_REQUEST = '@users/orgNameRequest',
    UF_REQUEST = '@users/ufRequest',
    ORG_LOGO_REQUEST = '@users/orgLogoRequest',
    LOGOS_USER_REQUEST = '@users/logsUserRequest',
}

/**
 * @memberof UsersTypes
 * @interface IUsersAction
 * @category React
 * @subcategory Redux / Types
 * @property {string} name Company data name.
 * @property {string} email Company data email.
 * @property {string} password Company data password.
 * @property {string} userId Company data userId.
 */
export interface IUsersAction {
    readonly name?: string
    readonly email?: string
    readonly password?: string
    readonly cpf?: string
    readonly phone?: string
    readonly institutional_position?: string
    readonly institutional_registration?: string
    readonly id?: string
    readonly organization_id?: string
    readonly active?: boolean
    readonly resources?: Array<string>
}

export interface IUserLoggedAction {
    readonly userId: string
    readonly userType: UserType
}

export interface IOrgAction {
    readonly name?: string
    readonly act_start_date?: string
    readonly act_end_date?: string
    readonly municipality?: string
    readonly uf_id?: string
    readonly email?: string
    readonly phone?: string
    readonly responsible?: string
    readonly address?: {
        readonly street?: string,
        readonly district?: string,
        readonly zip_code?: string,
        readonly complement?: string,
    }
    readonly responsible_sector?: string
    readonly id?: string | number
    readonly notes?: string
    readonly act?: FormData
    readonly userId?: string
    readonly logo?: FormData
}

/**
 * @memberof UsersTypes
 * @interface IUsersState
 * @category React
 * @subcategory Redux / Types
 * @property {ITabDiscribe} admins
 * @property {ITabDiscribe} analyst
 */
export interface IUsersState {
    readonly admins: ITabDiscribe
    readonly analyst: ITabDiscribe
}

export interface IUserLogged {
    readonly status: AsyncStateStatus
    readonly user: {
        readonly name: string
        readonly type: UsersTypes | string
        readonly email: string
        readonly id: string
        readonly cpf: string
        readonly organization_id: string
        readonly organization_logo: any
        readonly institutional_position: string
        readonly institutional_registration: string
        readonly phone: string
        readonly uf_logo: any
    }
}

interface ITabDiscribe {
    readonly request: {
        readonly data: Array<any>
        readonly status: AsyncStateStatus
    }
    readonly newUser: AsyncStateStatus
    readonly editUser: {
        readonly status: AsyncStateStatus,
        readonly data?: User
    }
    readonly changeResources?: AsyncStateStatus
}

export interface IOrg {
    readonly getAll: {
        readonly data: Array<any>
        readonly status: AsyncStateStatus
    }
    readonly newOrg: {
        readonly status: AsyncStateStatus
    }
    readonly getUsersByOrg: {
        readonly data: Array<any>
        readonly status: AsyncStateStatus
    }
    readonly orgResponsibles: {
        readonly status: AsyncStateStatus
    },
    readonly orgUpdate: {
        readonly status: AsyncStateStatus
    },
    readonly orgFind: {
        readonly status: AsyncStateStatus
        readonly data: any
    },
    readonly orgActUpload: {
        readonly status: AsyncStateStatus
        readonly idOrg: string | number
    },
    readonly orgActDownload: {
        readonly status: AsyncStateStatus
        readonly data: any
    },
    readonly orgActDelete: {
        readonly status: AsyncStateStatus
    }
}

/**
 * @memberof UsersTypes
 * @interface UsersState
 * @category React
 * @subcategory Redux / Types
 * @property {IUsersState} request
 * @property {IUserLogged} describe
 */
export interface UsersState {
    request: IUsersState
    userLogged: IUserLogged
    org: IOrg
    userActive: AsyncStateStatus
    analystRequest: AsyncStateStatus
    usersRequest: {
        status: AsyncStateStatus
        data: any
    }
    deleteUserRequest: AsyncStateStatus
    orgName: {
        status: AsyncStateStatus
        data: any
    }
    findUserRequest: {
        status: AsyncStateStatus
        data: any
    }
    ufRequest: {
        status: AsyncStateStatus
        data: any
    }
    orgLogo: AsyncStateStatus
    logsUser: {
        status: AsyncStateStatus,
        data: Array<any>
    }
}
